import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import type { ApplyContextValue } from '@kanda-libs/ks-apply';
import { useToast } from '@kanda-libs/ks-design-library';
import { Job, LeadApplicant, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import { selectors } from 'store';
import { reloadInitialData } from 'store/slices/app';

import { formatInitialCustomerData } from './helpers';

export interface ApplyProviderHook {
  value: ApplyContextValue;
  job?: Job;
}

export default function useApplyProvider(): ApplyProviderHook {
  const dispatch = useAppDispatch();
  const { showError } = useToast();
  const { push } = useHistory();
  const job = useSelector(selectors.job.getEntity);
  const { id } = useSelector(selectors.getPathKey);

  const onSuccess = useCallback(
    (body: LeadApplicant, cleanup: () => void) => {
      if (!job?.id) {
        showError('Something went wrong. Please try again later.');
      }

      dispatch(
        actions.quoteJob({
          params: { id: job?.id as string },
          body,
          forceReload: true,
          onSuccess: () => {
            dispatch(reloadInitialData());
            cleanup();
            push(URLS.quote.replace(':id', job?.id || ''));
          },
        }),
      );
    },
    [job, dispatch, showError, push],
  );

  const handleUrl = useCallback(
    (url: string) => url.replace(':id', id || ''),
    [id],
  );

  const value: ApplyContextValue = useMemo(
    () => ({
      id: 'checkout',
      storageKeyExtender: id,
      postQuoteLead: false,
      sendPixelEvents: false,
      showHeaderItem: () => true,
      showHeaderOnAllPages: true,
      headerItemPaths: [
        handleUrl(URLS.applyDetails),
        handleUrl(URLS.applyAddress),
        handleUrl(URLS.applyEmployment),
      ],
      redirectToStatusOnSuccess: false,
      initialCustomerDetails: formatInitialCustomerData(job),
      onClickNextSteps: () => {
        window.location.href = URLS.continueApplication.replace(
          ':id',
          id || '',
        );
      },
      urls: {
        details: handleUrl(URLS.applyDetails),
        address: handleUrl(URLS.applyAddress),
        employment: handleUrl(URLS.applyEmployment),
      },
      onSuccess: () => {},
      successOverride: onSuccess,
    }),
    [onSuccess, handleUrl, id, job],
  );

  // Effect redirects user if they have an AIP
  useEffect(() => {
    if (!job || !id) return;
    if (!job?.quoted_to) return;
    push(URLS.quote.replace(':id?', id));
  }, [job, id, push]);

  return {
    value,
    job,
  };
}
